import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { DoctorPricingRuleService } from '../../services/DoctorPricingRuleService';
import { ProfessionalPaymentService } from '../../services/ProfessionalPaymentService';
import { DoctorPricingRule } from '../../types/doctor-pricing-rule';

type ProfessionalOption = {
  doctorId: string;
  displayName: string;
};

const defaultForm = {
  doctorId: '',
  pricingRuleAppointmentLocation: 'Clinica' as DoctorPricingRule['pricingRuleAppointmentLocation'],
  pricingRuleAppointmentPaymentOrigin: 'pacote' as DoctorPricingRule['pricingRuleAppointmentPaymentOrigin'],
  pricingRulePricePerSession: '',
  pricingRuleIsActive: true,
};

const DoctorPricingRulesPage = () => {
  const { selectedClient } = useAuth();
  const clientId = selectedClient?.clientId;
  const canEdit = selectedClient?.userRole === 'admin';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState<DoctorPricingRule[]>([]);
  const [professionals, setProfessionals] = useState<ProfessionalOption[]>([]);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [form, setForm] = useState(defaultForm);

  const loadData = async () => {
    if (!clientId) {
      setRules([]);
      setProfessionals([]);
      return;
    }

    try {
      setLoading(true);
      const [loadedRules, loadedProfessionals] = await Promise.all([
        DoctorPricingRuleService.listByClient(clientId),
        ProfessionalPaymentService.getFisioterapeutas(clientId),
      ]);

      setRules(loadedRules);
      setProfessionals(
        loadedProfessionals
          .filter((professional) => professional.doctorId)
          .map((professional) => ({
            doctorId: professional.doctorId as string,
            displayName: professional.displayName,
          }))
      );
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || 'Erro ao carregar regras de repasse.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [clientId]);

  const resetForm = () => {
    setEditingRuleId(null);
    setForm(defaultForm);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!clientId || !canEdit || saving) return;
    if (!form.doctorId) {
      toast.error('Selecione um profissional.');
      return;
    }

    const price = Number(form.pricingRulePricePerSession);
    if (!Number.isFinite(price) || price < 0) {
      toast.error('Informe um valor válido por sessão.');
      return;
    }

    try {
      setSaving(true);
      await DoctorPricingRuleService.upsertRule({
        pricingRuleId: editingRuleId || undefined,
        clientId,
        doctorId: form.doctorId,
        pricingRuleAppointmentLocation: form.pricingRuleAppointmentLocation,
        pricingRuleAppointmentPaymentOrigin: form.pricingRuleAppointmentPaymentOrigin,
        pricingRulePricePerSession: price,
        pricingRuleIsActive: form.pricingRuleIsActive,
      });
      toast.success('Regra salva com sucesso.');
      resetForm();
      await loadData();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || 'Erro ao salvar regra.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rule: DoctorPricingRule) => {
    setEditingRuleId(rule.pricingRuleId);
    setForm({
      doctorId: rule.doctorId,
      pricingRuleAppointmentLocation: rule.pricingRuleAppointmentLocation,
      pricingRuleAppointmentPaymentOrigin: rule.pricingRuleAppointmentPaymentOrigin,
      pricingRulePricePerSession: String(rule.pricingRulePricePerSession),
      pricingRuleIsActive: rule.pricingRuleIsActive,
    });
  };

  const handleToggle = async (rule: DoctorPricingRule) => {
    if (!clientId || !canEdit || saving) return;
    setSaving(true);
    try {
      await DoctorPricingRuleService.toggleRuleActive(rule.pricingRuleId, clientId, !rule.pricingRuleIsActive);
      toast.success('Status atualizado.');
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao atualizar status.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (rule: DoctorPricingRule) => {
    if (!clientId || !canEdit || saving) return;
    if (!window.confirm('Excluir esta regra de repasse? Ela deixará de estar disponível para novos cálculos. Os repasses e valores já registrados serão preservados.')) return;
    setSaving(true);
    try {
      await DoctorPricingRuleService.deleteRule(rule.pricingRuleId, clientId);
      if (editingRuleId === rule.pricingRuleId) resetForm();
      toast.success('Regra excluída.');
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao excluir regra.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Regras de Repasse</h2>
          <p className="text-sm text-gray-500">Cadastre valores por profissional, local e origem do atendimento.</p>
        </div>
        <button
          onClick={resetForm}
          className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nova Regra
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={handleSubmit} className="rounded-lg border bg-white p-6 shadow-sm space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Profissional</label>
            <select
              value={form.doctorId}
              onChange={(e) => setForm((prev) => ({ ...prev, doctorId: e.target.value }))}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Selecione</option>
              {professionals.map((professional) => (
                <option key={professional.doctorId} value={professional.doctorId}>
                  {professional.displayName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Local</label>
            <select
              value={form.pricingRuleAppointmentLocation}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  pricingRuleAppointmentLocation: e.target.value as typeof form.pricingRuleAppointmentLocation,
                }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="Clinica">Clínica</option>
              <option value="Domicilio">Domicílio</option>
              <option value="Hospitalar">Hospitalar</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Origem</label>
            <select
              value={form.pricingRuleAppointmentPaymentOrigin}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  pricingRuleAppointmentPaymentOrigin: e.target.value as typeof form.pricingRuleAppointmentPaymentOrigin,
                }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="pacote">pacote</option>
              <option value="avulsa">avulsa</option>
              <option value="sem_repasse">sem_repasse</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Valor por Sessão</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.pricingRulePricePerSession}
              onChange={(e) => setForm((prev) => ({ ...prev, pricingRulePricePerSession: e.target.value }))}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.pricingRuleIsActive}
              onChange={(e) => setForm((prev) => ({ ...prev, pricingRuleIsActive: e.target.checked }))}
            />
            Regra ativa
          </label>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving || !canEdit}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {editingRuleId ? 'Atualizar' : 'Salvar'}
            </button>
            {editingRuleId && (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-md border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>

        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-gray-800">Regras cadastradas</h3>
          {loading ? (
            <p className="text-sm text-gray-500">Carregando...</p>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div key={rule.pricingRuleId} className="rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-gray-900">{professionalNameById(professionals, rule.doctorId)}</p>
                      <p className="text-sm text-gray-600">
                        {rule.pricingRuleAppointmentLocation} · {rule.pricingRuleAppointmentPaymentOrigin}
                      </p>
                      <p className="text-sm font-semibold text-primary-600">
                        R$ {Number(rule.pricingRulePricePerSession).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {canEdit && <><button type="button" disabled={saving} onClick={() => handleEdit(rule)} aria-label="Editar regra de repasse" className="rounded-md p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button type="button" disabled={saving} onClick={() => void handleToggle(rule)} aria-label="Alternar situação da regra de repasse" className="rounded-md p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50">
                        {rule.pricingRuleIsActive ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                      </button>
                      <button type="button" disabled={saving} onClick={() => void handleDelete(rule)} aria-label="Excluir regra de repasse" className="inline-flex items-center gap-1 rounded-md p-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50">
                        <Trash2 className="h-4 w-4" />Excluir
                      </button></>}
                    </div>
                  </div>
                </div>
              ))}
              {rules.length === 0 && <p className="text-sm text-gray-500">Nenhuma regra cadastrada.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function professionalNameById(professionals: ProfessionalOption[], doctorId: string): string {
  return professionals.find((professional) => professional.doctorId === doctorId)?.displayName || doctorId;
}

export default DoctorPricingRulesPage;
